package com.dotdrives.client

import com.dotdrives.model.DriveHealthEntry
import com.dotdrives.model.PhaseChangeSample
import com.dotdrives.model.phaseMedians
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.FilterOperator
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * "Drives sitting in a phase longer than your team's median" (docs/schema.md,
 * "Stale-drive-nudge specifically"). Two numbers from two different places, per the schema's
 * own framing: *how long this drive has sat here* is `now() - drives.phase_entered_at` (a
 * cheap current-state read); *what "normal" looks like for that phase* is a median computed
 * from history -- the time between consecutive `drive.phase_changed` events per drive,
 * aggregated per phase with `percentile_cont(0.5)` (see `model/PipelineHealthMath.kt` for the
 * pure aggregate, tested directly there). The real evaluate-rules job runs that aggregate as a
 * stable SQL function; this client computes the same aggregate over the same source rows in
 * Kotlin, since no such function is exposed to the web client today. This is a known scaling
 * limitation for a large event history -- see the phase-1 report -- and a candidate for a
 * dedicated RPC later, not a difference in what "median" means. The event query orders
 * `Order.DESCENDING` so the `limit` below keeps the *newest* history under a large event
 * count -- ascending order would let the median permanently reflect an org's earliest history
 * and never track its current pace, which is the entire point of this panel.
 */
class PipelineHealthClient(
    private val client: SupabaseClient = SupabaseProvider.client,
) {
    suspend fun list(orgId: String): List<DriveHealthEntry> {
        val drives = client.from(DRIVES_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    filter("archived_at", FilterOperator.IS, null)
                    filter("outcome", FilterOperator.IS, null)
                }
            }
            .decodeList<HealthDriveRow>()
        if (drives.isEmpty()) return emptyList()

        val events = client.from(ACTIVITY_EVENTS_TABLE)
            .select {
                limit(EVENT_HISTORY_LIMIT)
                order("occurred_at", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("subject_type", "drive")
                    eq("verb", "drive.phase_changed")
                }
            }
            .decodeList<PhaseChangeEventRow>()

        val samples = events.mapNotNull { event ->
            val toPhaseId = event.payload["to_phase_id"]?.jsonPrimitive?.content ?: return@mapNotNull null
            PhaseChangeSample(driveId = event.subjectId, toPhaseId = toPhaseId, occurredAt = event.occurredAt)
        }
        val medianByPhase = phaseMedians(samples)
        val phaseNames = loadPhaseNames(orgId, drives.map(HealthDriveRow::currentPhaseId).distinct())
        val now = Clock.System.now()

        return drives.mapNotNull { drive ->
            val median = medianByPhase[drive.currentPhaseId] ?: return@mapNotNull null
            val daysInPhase = (now - drive.phaseEnteredAt).inWholeDays
            if (median <= 0.0 || daysInPhase < median * STALE_MULTIPLIER) return@mapNotNull null
            val phaseName = phaseNames[drive.currentPhaseId] ?: "this phase"
            DriveHealthEntry(
                driveId = drive.id,
                driveName = drive.title,
                phaseId = drive.currentPhaseId,
                phaseName = phaseName,
                daysInPhase = daysInPhase,
                medianDays = median,
                suggestion = "It's been in $phaseName twice as long as your team's usual pace — worth a nudge.",
            )
        }.sortedByDescending { it.daysInPhase - it.medianDays }
    }

    private suspend fun loadPhaseNames(orgId: String, phaseIds: List<String>): Map<String, String> {
        if (phaseIds.isEmpty()) return emptyMap()
        return client.from(PHASES_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    isIn("id", phaseIds)
                }
            }
            .decodeList<HealthPhaseNameRow>()
            .associate { it.id to it.name }
    }
}

@Serializable
private data class HealthDriveRow(
    val id: String,
    val title: String,
    @SerialName("current_phase_id")
    val currentPhaseId: String,
    @SerialName("phase_entered_at")
    val phaseEnteredAt: Instant,
)

@Serializable
private data class PhaseChangeEventRow(
    @SerialName("subject_id")
    val subjectId: String,
    val payload: JsonObject,
    @SerialName("occurred_at")
    val occurredAt: Instant,
)

@Serializable
private data class HealthPhaseNameRow(
    val id: String,
    val name: String,
)

private const val DRIVES_TABLE = "drives"
private const val ACTIVITY_EVENTS_TABLE = "activity_events"
private const val PHASES_TABLE = "drive_phases"
private const val EVENT_HISTORY_LIMIT = 2000L
private const val STALE_MULTIPLIER = 2.0
